export default class MultiColumnPrintHelper {
  constructor({ pdf, doc, backgroundBase64Images, renderImage, renderText, drawBackground }) {
    this.pdf = pdf;
    this.doc = doc;
    this.backgroundBase64Images = backgroundBase64Images;
    this.renderImage = renderImage;
    this.renderText = renderText;
    this.drawBackground = drawBackground;
  }

  // Public entry point
  render(columns = 2) {
    columns = Number(columns) || 2;
    if (columns < 1) columns = 1;

    for (let pageIndex = 0; pageIndex < this.doc.pages.length; pageIndex++) {
      if (this.backgroundBase64Images && this.backgroundBase64Images.length > 0) {
        const img = this.backgroundBase64Images[Math.min(pageIndex, this.backgroundBase64Images.length - 1)];
        this.drawBackground(this.pdf, this.doc, img);
      }

      this._renderMultiColumnPrintLinesOnPage(pageIndex, columns);

      if (this.doc.pages[pageIndex + 1]) {
        this.pdf.addPage();
      }
    }
  }

  // Internal helpers (private methods)
  _sortByBorderThenImageThenText(printLines) {
    return printLines.sort((a, b) => {
      // prioritize border first
      if (a.field === 'border' && b.field !== 'border') return -1;
      if (b.field === 'border' && a.field !== 'border') return 1;

      // images first, then text to ensure render order
      if (a.type !== b.type) {
        return a.type === 'IMAGE' ? -1 : 1;
      }

      return a.id - b.id;
    });
  }

  _drawBorderOnPrintLine(printLine) {
    this.pdf.setDrawColor(0, 0, 0);
    this.pdf.setLineWidth(0.25);
    this.pdf.rect(printLine.x, printLine.y, printLine.width, printLine.height);
  }

  _computeHeightOfFirstEntry(printLinesByRowNo, sortedRowNumbers) {
    const rowNoOfFirstEntry = sortedRowNumbers.length > 0 ? sortedRowNumbers[0] : null;
    const printLinesOfFirstEntry = rowNoOfFirstEntry !== null ? printLinesByRowNo[rowNoOfFirstEntry] : [];

    const minTopY = (printLinesOfFirstEntry && printLinesOfFirstEntry.length)
      ? Math.min(...printLinesOfFirstEntry.map(l => l.y)) : null;
    const maxBottomY = (printLinesOfFirstEntry && printLinesOfFirstEntry.length)
      ? Math.max(...printLinesOfFirstEntry.map(l => {
        const h = (l.type === 'IMAGE' && Number.isFinite(l.height)) ? l.height : 0;
        return l.y + h;
      }))
      : null;
    return (Number.isFinite(maxBottomY) && Number.isFinite(minTopY)) ? (maxBottomY - minTopY) : null;
  }

  // The base y-offset is the height of first entry + y spacing. This y-offset will be added from 2nd row onwards.
  _computeBaseYOffset(printLinesByRowNo, sortedRowNumbers) {
    const ySpacingAttrArray = this.doc.attributes.filter(attr => attr.type === 'MULTILINE_Y_SPACING');
    const ySpacing = ySpacingAttrArray.length ? parseInt(ySpacingAttrArray[0].value) : 0;

    const heightOfFirstEntry = this._computeHeightOfFirstEntry(printLinesByRowNo, sortedRowNumbers);

    return Number.isFinite(heightOfFirstEntry) ? (heightOfFirstEntry + ySpacing) : ySpacing;
  }

  // Compute x offset based on the index of the column within the current page.
  // This ensures that for each new page, columnNo starts from 0 and x resets to the initial position.
  _computeXOffset(rowIndexWithinPage, columns, columnWidth) {
    const columnNo = rowIndexWithinPage % columns;

    return columnNo * columnWidth;
  }

  // Compute y offset based on the index of the row within the current page.
  // This ensures that for each new page, rowIndex starts from 0 and x resets to the initial position.
  _computeYOffset(rowIndexWithinPage, baseYOffset, columns) {
    const rowIndex = Math.floor(rowIndexWithinPage / columns);

    return rowIndex > 0 ? rowIndex * baseYOffset : 0;
  }

  /**
   * VISUAL DIAGRAM: Rendering print entries in N customizable columns
   *
   * Terminology
   * - fieldRowNo (rowNo): row number or row index of a single row entry.
   * - columns:       Number of columns to render per page (configurable).
   * - columnWidth:   pageWidth / columns. example: 480mm (pageWidth) / 4 columns = 120mm (columnWidth)
   * - baseYOffset:   Height of the first entry + Y spacing; applied from 2nd rendered row onwards.
   * - xOffset:       Horizontal shift based on column position within the page.
   * - yOffset:       Vertical shift based on the row index within the page.
   *
   * What happens (high-level):
   * 1) Active print lines are grouped by fieldRowNo (row numbers).
   * 2) baseYOffset is computed as: height(first entry) + y spacing.
   * 3) For each entry, x-offset is computed from its column index within the page.
   * 4) For each entry, y-offset is computed as rowIndexWithinPage * baseYOffset (row index increases after filling all columns).
   *
   * Indexing per page
   * - idx: increments by 1 for each rowNo rendered on the current page.
   * - columnNo = idx % columns.
   * - rowIndexWithinPage = floor(idx / columns).
   *
   * Offsets
   * - xOffset = columnNo * columnWidth.
   * - yOffset = rowIndexWithinPage > 0 ? rowIndexWithinPage * baseYOffset : 0.
   *
   *
   * Example with columns = 2 (two columns per page)
   *
   *   Page width
   *   |<----------- columnWidth ----------->|<----------- columnWidth ----------->|
   *   |            COLUMN 0                 |             COLUMN 1                |
   *   |                                     |                                     |
   *   V y
   *   +---------------------------------------------------------------------------------> x
   *   |  idx=0, rowIndex=0, columnNo=0                                            |
   *   |  xOffset = 0 * columnWidth = 0                                            |
   *   |  yOffset = 0 * baseYOffset = 0                                            |
   *   |  (first entry at natural y)                                               |
   *   |                                                                           |
   *   |                          idx=1, rowIndex=0, columnNo=1                    |
   *   |                          xOffset = 1 * columnWidth                        |
   *   |                          yOffset = 0                                      |
   *   |                                                                           |
   *   |  idx=2, rowIndex=1, columnNo=0                                            |
   *   |  xOffset = 0 * columnWidth = 0                                            |
   *   |  yOffset = 1 * baseYOffset                                                |
   *   |                                                                           |
   *   |                          idx=3, rowIndex=1, columnNo=1                    |
   *   |                          xOffset = 1 * columnWidth                        |
   *   |                          yOffset = 1 * baseYOffset                        |
   *   |                                                                           |
   *   |  ... and so on                                                            |
   *
   * Example with columns = 3
   *
   *   |<---- col 0 ---->|<---- col 1 ----->|<---- col 2 ----->|
   *    idx=0  (r=0,c=0)   idx=1  (r=0,c=1)   idx=2  (r=0,c=2)
   *    idx=3  (r=1,c=0)   idx=4  (r=1,c=1)   idx=5  (r=1,c=2)
   *    idx=6  (r=2,c=0)   idx=7  (r=2,c=1)   idx=8  (r=2,c=2)
   *    where r = rowIndexWithinPage = floor(idx / columns), c = columnNo = idx % columns
   *
   * Height of the first entry
   * - Determined from the first row (lowest rowNo):
   *   height = max(y + h) - min(y) for that entry's lines (h is image height when available; text contributes 0 to height here).
   * - baseYOffset = height(first entry) + ySpacing (MULTILINE_Y_SPACING attribute).
   *
   * Per-entry rendering sequence
   * - Lines within a rowNo are sorted: border first, then images, then text by id.
   * - For each line in that rowNo:
   *   • Shift x by xOffset (places the entry into the correct column).
   *   • Shift y by yOffset for rows beyond the first rendered row.
   *   • Render image (if any) → draw border (if applicable) → render text.
   *
   * Page boundaries
   * - The column and row indices (columnNo, rowIndexWithinPage) reset on each new page.
   * - New pages are added as defined by the outer render() loop.
   */
  _renderMultiColumnPrintLinesOnPage(pageIndex, columns) {
    const activeLines = this.doc.pages[pageIndex].lines.filter(l => l.active);
    const pageWidth = this.doc.orientation === 'PORTRAIT' ? this.doc.shortEdge : this.doc.longEdge;
    const columnWidth = pageWidth / columns;

    const printLinesByRowNo = Object.groupBy(activeLines, l => l.fieldRowNo);
    const sortedRowNumbers = Object.keys(printLinesByRowNo)
      .sort((a, b) => a - b)
      .map(a => parseInt(a));

    const baseYOffset = this._computeBaseYOffset(printLinesByRowNo, sortedRowNumbers);

    for (let idx = 0; idx < sortedRowNumbers.length; idx++) {
      const rowNo = sortedRowNumbers[idx];
      const printLines = this._sortByBorderThenImageThenText(printLinesByRowNo[rowNo]);

      // Determine column and row offsets based on the index within the current page
      const xOffsetForColumnNo = this._computeXOffset(idx, columns, columnWidth);
      const yOffsetForRowNo = this._computeYOffset(idx, baseYOffset, columns);

      for (const printLine of printLines) {
        // move x to the corresponding column
        const originalX = printLine.x;
        printLine.x = originalX + xOffsetForColumnNo;

        // apply y-offset to 2nd row onwards
        if (yOffsetForRowNo) {
          printLine.y = printLine.y + yOffsetForRowNo;
        }

        this.renderImage(this.pdf, printLine);
        if (printLine.field === 'border' && printLine.type === 'IMAGE') {
          this._drawBorderOnPrintLine(printLine);
        }
        this.renderText(this.pdf, printLine);
      }
    }
  }
}
